import { defineRule } from 'vee-validate';
import PhoneNumber from 'awesome-phonenumber';
import {
  required,
  digits,
  confirmed,
  email,
  numeric,
} from '@vee-validate/rules';
import zipCodes from '~/helpers/validation/zipCodesValidationRules.json';

const calculateAge = (birthday: Date) => {
  const currentDate = new Date();
  const diff = currentDate.valueOf() - birthday.valueOf();
  // This is the difference in milliseconds
  const currentAge = Math.floor(diff / 31557600000);
  // Divide by 1000*60*60*24*365.25
  return currentAge;
};

const vatCountryConfigs: Record<string, { allowedSeparators: string[]; amountMinDigits: number }> = {
  AT: {
    amountMinDigits: 9,
    allowedSeparators: ['-'],
  },
  BG: {
    amountMinDigits: 9,
    allowedSeparators: ['-'],
  },
  CZ: {
    amountMinDigits: 8,
    allowedSeparators: ['-'],
  },
  DE: {
    amountMinDigits: 9,
    allowedSeparators: ['-'],
  },
  ES: {
    amountMinDigits: 9,
    allowedSeparators: ['-'],
  },
  HR: {
    amountMinDigits: 11,
    allowedSeparators: ['-'],
  },
  HU: {
    amountMinDigits: 8,
    allowedSeparators: ['-'],
  },
  IT: {
    amountMinDigits: 11,
    allowedSeparators: ['-'],
  },
  NL: {
    amountMinDigits: 12,
    allowedSeparators: ['-'],
  },
  PL: {
    amountMinDigits: 10,
    allowedSeparators: ['-'],
  },
  PT: {
    amountMinDigits: 9,
    allowedSeparators: ['-'],
  },
  RO: {
    amountMinDigits: 9,
    allowedSeparators: ['-'],
  },
  SI: {
    amountMinDigits: 8,
    allowedSeparators: ['-'],
  },
  SK: {
    amountMinDigits: 10,
    allowedSeparators: ['-'],
  },
};

export default defineNuxtPlugin(async () => {
  const { $i18n, $config, $domain } = useNuxtApp();

  defineRule('phone', (value: string = '') => {
    const phone = PhoneNumber.parsePhoneNumber(value, { regionCode: $domain.countryCode });

    if (!phone.valid) {
      return $i18n.t('This is not a valid phone number');
    }

    return true;
  });

  defineRule('required', (value: string = '') => required(value) || $i18n.t('This field is required.'));

  defineRule('length', (value: string = '', [length]: string[]) => {
    if (value.length === parseInt(length, 10)) {
      return true;
    }

    return $i18n.t('The field should have exactly X characters.', { length });
  });

  defineRule('minmax', (value: string = '', [min, max]: string[]) => {
    if (value?.length >= parseInt(min, 10) && value?.length <= parseInt(max, 10)) {
      return true;
    };

    return $i18n.t('The field should have between {min} and {max} characters.', { min, max });
  });

  defineRule('max', (value: string = '', [max]: string[]) => {
    if (value.length <= parseInt(max, 10)) {
      return true;
    }

    return $i18n.t('The field should not contain more than {max} characters.', { max });
  });

  defineRule('digits', (value: string = '', params: [string]) => digits(value, params) || $i18n.t('This field should only contain numbers.'));

  defineRule('confirmed', (value, [target]) => {
    if (value === target) {
      return true;
    }

    return confirmed(value, [target]) || $i18n.t('The field does not match.');
  });

  defineRule('email', (value: string = '') => {
    const hasSpecialChars = /[^\w\d@!#$%&'*+-/=?^_`.{|}~]/.test(value);
    return (email(value) && !hasSpecialChars) ?? $i18n.t('The e-mail address is incorrect.');
  });

  defineRule('numeric', (value: string = '') => numeric(value) ?? $i18n.t('This field should only contain numbers.'));

  defineRule('password', (value: string = '') => {
    const lengthValid = value.length >= 8 && value.length <= 18;
    const hasUppercase = /[A-Z]/.test(value);
    const hasLowercase = /[a-z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[~!@#$%^&*_\-+='|(){}[\]:;"<>,.?/]/.test(value);

    if (!lengthValid || !hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
      return $i18n.t('passwordNotValid');
    }

    return true;
  });

  defineRule('dateUntilToday', (value: string = '') => {
    const selectedDate = new Date(value);
    const now = new Date();

    return selectedDate.valueOf() < now.valueOf() || $i18n.t('Date should not be in the future.');
  });

  defineRule('dateFormat', (value: string = '') => /\d{4}-\d{2}-\d{2}/.test(value) ?? $i18n.t('Date should be in the format YYYY-MM-DD.'));
  defineRule('validBirthDate', (value: string = '') => {
    const selectedDate = new Date(value);
    const now = new Date();
    const lessThen100Y = selectedDate.getFullYear() >= now.getFullYear() - 100;
    return (selectedDate.valueOf() < now.valueOf() || lessThen100Y) ?? $i18n.t('Please provide a valid birth date');
  });

  defineRule('atAgeLimit', (value: string = '', [ageLimit]: [string]) => {
    const max = parseInt(ageLimit, 10);
    const selectedDate = new Date(value);
    return calculateAge(selectedDate) >= max || $i18n.t('Sorry! You should have at least {ageLimit} years to continue with this operation', { ageLimit });
  });

  defineRule('validZipCode', (value: string = '', [country = '']: string[]) => {
    const countryZipCodeRegex = zipCodes[country.toUpperCase()];
    const zipCodeRegex = RegExp(countryZipCodeRegex);

    if (country && countryZipCodeRegex && zipCodeRegex.test(value)) {
      return true;
    }

    const countryName = $config.public.i18n.countries.find(({ name }) => name === country)?.label;

    if (!countryName) {
      return $i18n.t('Postal code format is not correct');
    }

    return $i18n.t('Postal code format is not correct for {country}', { country: $i18n.t(countryName) });
  });

  defineRule('noEmojis', (value: string = '') => {
    if (!(/\p{Extended_Pictographic}/u.test(value))) {
      return true;
    }

    return $i18n.t('Emojis are not allowed');
  });

  defineRule('vatFormat', (insertedVAT: string, [country]: [string]) => {
    const { allowedSeparators, amountMinDigits } = vatCountryConfigs[country.toUpperCase()];
    // check for non digits or non allowed separators
    const regex = new RegExp(`[^\\d${allowedSeparators.join('')}]`, 'g');

    const insertedDigits = insertedVAT.replaceAll(/[^\d]/g, '');
    if (regex.exec(insertedVAT) === null && amountMinDigits === insertedDigits.length) {
      return true;
    }

    return $i18n.t('VAT format is invalid', { digits: amountMinDigits, insertedDigits: insertedDigits.length, nonDigitsAllowed: allowedSeparators.join('') });
  });
});
